import { getValidFoodIntolerances } from '@/lib/clientPreferenceOptions';
import { formatDisplayLabel } from '@/utils/display-labels';

const normalizeNoneText = (value: string): string =>
  String(value ?? '')
    .toLowerCase()
    .replace(/[_/\\-]+/g, ' ')
    .replace(/[^a-z0-9\s]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

/** Detect "None" / "No intolerances" style options that must be exclusive. */
export function isNoneFoodIntoleranceValue(value: string): boolean {
  const normalized = normalizeNoneText(value);
  if (!normalized) return false;

  return (
    normalized === 'none' ||
    normalized === 'no' ||
    normalized === 'na' ||
    normalized === 'n a' ||
    normalized === 'no food intolerances' ||
    normalized === 'no intolerances' ||
    normalized.startsWith('none ') ||
    normalized.startsWith('no known ') ||
    normalized.includes('none of the above') ||
    normalized.includes('none of these')
  );
}

/**
 * Keep Food Intolerances "None" mutually exclusive with real intolerances:
 * - selecting None clears every other selection
 * - selecting any real intolerance while None is selected clears None
 */
export function normalizeFoodIntoleranceSelection(
  nextSelected: string[],
  previousSelected: string[] = [],
): string[] {
  const next = nextSelected.map((value) => String(value ?? '').trim()).filter(Boolean);
  const previous = previousSelected.map((value) => String(value ?? '').trim()).filter(Boolean);

  const nextNone = next.find(isNoneFoodIntoleranceValue);
  const previousHadNone = previous.some(isNoneFoodIntoleranceValue);
  const nextNonNone = next.filter((value) => !isNoneFoodIntoleranceValue(value));
  const addedNonNone = nextNonNone.filter(
    (value) => !previous.some((item) => item.toLowerCase() === value.toLowerCase()),
  );

  // User just selected None → keep only None.
  if (nextNone && !previousHadNone) {
    return [nextNone];
  }

  // User selected a real intolerance while None was on → drop None.
  if (previousHadNone && addedNonNone.length > 0) {
    return nextNonNone;
  }

  // Stale mix (None + others) → keep only None.
  if (nextNone && nextNonNone.length > 0) {
    return [nextNone];
  }

  return nextNone ? [nextNone] : nextNonNone;
}

export function resolveFoodIntoleranceValue(value: string): string {
  const normalized = value.trim().toLowerCase();
  const matched = getValidFoodIntolerances().find(
    (intolerance) =>
      intolerance.value.toLowerCase() === normalized ||
      intolerance.name.toLowerCase() === normalized,
  );
  return matched?.value ?? value.trim();
}

export function resolveFoodIntoleranceLabel(value: string): string {
  const normalized = value.trim().toLowerCase();
  const matched = getValidFoodIntolerances().find(
    (intolerance) =>
      intolerance.value.toLowerCase() === normalized ||
      intolerance.name.toLowerCase() === normalized,
  );
  return matched?.name ?? formatDisplayLabel(value);
}

export function toFoodIntoleranceApiLabels(values: string[] | undefined | null): string[] {
  const seen = new Set<string>();
  const labels: string[] = [];

  (values ?? []).forEach((value) => {
    // "None" means no intolerances — never send it as a real intolerance label.
    if (isNoneFoodIntoleranceValue(String(value ?? ''))) return;

    const label = resolveFoodIntoleranceLabel(String(value ?? ''));
    if (!label || isNoneFoodIntoleranceValue(label)) return;
    const key = label.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    labels.push(label);
  });

  return labels;
}
